// Dzwiek w grze: krotkie, syntezowane sygnaly zwyciestwa i porazki (Web Audio API).
//
// Zero plikow audio - melodie skladamy z pojedynczych oscylatorow, wiec nie ma
// czego pobierac ani cache'owac, a bundle nie rosnie. Kazdy blad (brak wsparcia,
// zablokowany AudioContext) po cichu pomijamy - gra dziala dalej bez dzwieku,
// dokladnie tak jak przy haptyce (app/haptics.ts).
//
// Wlacz/wylacz zapamietujemy osobno od stanu gry (wlasny klucz w localStorage),
// zeby "glosniczek" nie znikal przy zmianie planszy ani resetach konstrukcji.

import { safeStorage } from './persistence';

export const SOUND_KEY = 'robot-lab.sound.v1';

export type OutcomeSound = 'won' | 'failed';

/** Pojedynczy ton: czestotliwosc (Hz), start i dlugosc (s), typ fali, glosnosc. */
export interface Tone {
  frequency: number;
  startSec: number;
  durationSec: number;
  wave: OscillatorType;
  gain: number;
}

/** Fanfara zwyciestwa: wznoszace sie arpeggio (C-dur). */
export const WIN_SOUND: readonly Tone[] = [
  { frequency: 523.25, startSec: 0.0, durationSec: 0.18, wave: 'triangle', gain: 0.18 }, // C5
  { frequency: 659.25, startSec: 0.11, durationSec: 0.18, wave: 'triangle', gain: 0.18 }, // E5
  { frequency: 783.99, startSec: 0.22, durationSec: 0.2, wave: 'triangle', gain: 0.18 }, // G5
  { frequency: 1046.5, startSec: 0.33, durationSec: 0.42, wave: 'triangle', gain: 0.2 }, // C6
];

/** Porazka: opadajaca, "brzeczaca" sekwencja - jednoznacznie negatywna. */
export const LOSE_SOUND: readonly Tone[] = [
  { frequency: 392.0, startSec: 0.0, durationSec: 0.2, wave: 'sawtooth', gain: 0.12 }, // G4
  { frequency: 311.13, startSec: 0.14, durationSec: 0.24, wave: 'sawtooth', gain: 0.12 }, // Eb4
  { frequency: 233.08, startSec: 0.3, durationSec: 0.5, wave: 'sawtooth', gain: 0.13 }, // Bb3
];

/** Melodia dla wyniku proby. */
export function soundFor(outcome: OutcomeSound): readonly Tone[] {
  return outcome === 'won' ? WIN_SOUND : LOSE_SOUND;
}

// --- minimalny kontrakt Web Audio (testowalny bez przegladarki) ---

interface AudioParamLike {
  setValueAtTime(value: number, time: number): void;
  linearRampToValueAtTime(value: number, time: number): void;
}

export interface OscillatorLike {
  type: string;
  frequency: AudioParamLike;
  connect(target: unknown): void;
  start(time: number): void;
  stop(time: number): void;
}

export interface GainLike {
  gain: AudioParamLike;
  connect(target: unknown): void;
}

export interface AudioContextLike {
  currentTime: number;
  /** 'running' | 'suspended' | 'closed' w prawdziwym AudioContext. */
  state: string;
  destination: unknown;
  resume?(): Promise<unknown>;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
}

type AudioContextConstructor = new () => AudioContextLike;

/** Prefiks `webkit` obsluguje starsze Safari/iOS. Brak obu = brak wsparcia. */
function resolveAudioContextCtor(): AudioContextConstructor | null {
  const scope = globalThis as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

let cachedContext: AudioContextLike | null = null;

/** Leniwie tworzy i cache'uje AudioContext. Brak wsparcia/blad = null. */
function getContext(): AudioContextLike | null {
  if (cachedContext) return cachedContext;
  const Ctor = resolveAudioContextCtor();
  if (!Ctor) return null;
  try {
    cachedContext = new Ctor();
  } catch {
    cachedContext = null;
  }
  return cachedContext;
}

/**
 * Wznawia AudioContext po gescie uzytkownika - iOS/Chrome blokuja dzwiek do
 * pierwszego dotkniecia (u nas jest nim "Testuj na trase"). Bez wsparcia nic
 * sie nie dzieje.
 */
export function resumeAudio(): void {
  const context = getContext();
  if (!context || context.state === 'running') return;
  if (typeof context.resume !== 'function') return;
  try {
    void context.resume().catch(() => undefined);
  } catch {
    // AudioContext.resume moze rzucic synchronicznie - ignorujemy.
  }
}

/**
 * Odtwarza sekwencje tonow. Zwraca true, gdy dzwiek zostal zlecony.
 * Brak wsparcia, pusty zestaw albo blad = ciche false.
 */
export function playSound(
  tones: readonly Tone[],
  context: AudioContextLike | null = getContext(),
): boolean {
  if (!context || tones.length === 0) return false;
  try {
    const start = context.currentTime;
    for (const tone of tones) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const from = start + tone.startSec;
      const until = from + tone.durationSec;

      oscillator.type = tone.wave;
      oscillator.frequency.setValueAtTime(tone.frequency, from);
      // Krotka obwiednia glosnosci: bez niej start/stop tonu strzela w glosnikach.
      gain.gain.setValueAtTime(0.0001, from);
      gain.gain.linearRampToValueAtTime(tone.gain, from + 0.012);
      gain.gain.linearRampToValueAtTime(0.0001, until);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(from);
      oscillator.stop(until + 0.02);
    }
    return true;
  } catch {
    return false;
  }
}

/** Dzwiek wyniku proby. Wyciszony gracz = nic (i nie dotykamy AudioContext). */
export function playOutcome(
  outcome: OutcomeSound,
  muted: boolean,
  context?: AudioContextLike | null,
): boolean {
  if (muted) return false;
  return playSound(soundFor(outcome), context ?? getContext());
}

// --- wyciszenie (zapamietywane w localStorage) ---

/**
 * Czy dzwiek jest wyciszony. Brak zapisu (pierwsze uruchomienie) albo
 * uszkodzone dane = dzwiek WLACZONY (false).
 */
export function loadMuted(storage: Storage | null = safeStorage()): boolean {
  if (!storage) return false;
  let raw: string | null = null;
  try {
    raw = storage.getItem(SOUND_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return false;
    return (parsed as Record<string, unknown>).muted === true;
  } catch {
    return false;
  }
}

/** Zapisuje wybor gracza. Brak localStorage = po cichu pomijamy. */
export function saveMuted(muted: boolean, storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(SOUND_KEY, JSON.stringify({ muted }));
  } catch {
    // brak zapisu nie jest bledem gry - wybor dziala do konca sesji
  }
}
